import {
	ArrowDown,
	ArrowRight,
	Bell,
	Check,
	ChevronRight,
	CircleAlert,
	FileSearch,
	FileSpreadsheet,
	GitMerge,
	Landmark,
	Network,
	ScrollText,
	Search,
	ShieldCheck,
	TrendingUp,
} from "lucide-react";
import { Link } from "react-router";
import { CompanyAutocomplete } from "~/components/search/company-autocomplete";
import { WebSiteJsonLd } from "~/components/seo/website-jsonld";
import { useAuth } from "~/lib/hooks/use-auth";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "FirmIndex – Firemní finance jsou jen začátek",
		description:
			"Finance, firemní listiny a důležité změny českých firem v souvislostech. Vyzkoušejte FirmIndex Premium na 14 dní zdarma a bez platební karty.",
		canonical: SITE_URL,
	});
}

const workflowSteps = [
	{
		number: "01",
		icon: Search,
		title: "Najděte firmu",
		description: "Stačí název nebo IČO. Základní profil si můžete prohlédnout i bez registrace.",
	},
	{
		number: "02",
		icon: FileSearch,
		title: "Pochopte souvislosti",
		description:
			"Projděte finance, osoby ve vedení, veřejné listiny a další dostupné údaje na jednom místě.",
	},
	{
		number: "03",
		icon: Bell,
		title: "Sledujte, co se mění",
		description: "Přidejte si důležité firmy do monitoringu a mějte jejich změny po ruce.",
	},
];

const documentInsights = [
	{
		icon: GitMerge,
		title: "Co se ve firmě chystá změnit",
		description:
			"Z notářských zápisů a projektů přeměn převádíme do přehledu fúze, změny kapitálu, likvidace a další zveřejněné události.",
	},
	{
		icon: ScrollText,
		title: "Co skutečně říkají stanovy",
		description:
			"Pravidla jednání, převoditelnost podílů, předkupní práva nebo pravidla rozdělování zisku bez ručního pročítání celého dokumentu.",
	},
	{
		icon: Network,
		title: "Kdo je s kým propojený",
		description:
			"Ze zpráv o vztazích zpřehledňujeme ovládající a propojené osoby, významné vztahy uvnitř skupiny i vyjádření k případné újmě.",
	},
];

const publicDataSources = [
	{
		icon: Landmark,
		label: "Veřejné registry",
	},
	{
		icon: FileSpreadsheet,
		label: "Účetní závěrky",
	},
	{
		icon: ScrollText,
		label: "Firemní listiny",
	},
	{
		icon: ShieldCheck,
		label: "Insolvence a DPH",
	},
];

const connectedInsights = [
	{
		icon: TrendingUp,
		label: "Finance v čase",
	},
	{
		icon: GitMerge,
		label: "Firemní události",
	},
	{
		icon: Network,
		label: "Lidé a vztahy",
	},
	{
		icon: Bell,
		label: "Monitoring změn",
	},
];

function TrialLink({ children, className }: { children: React.ReactNode; className: string }) {
	const { user, loading } = useAuth();

	if (loading) {
		return <span className={`${className} opacity-70`}>{children}</span>;
	}

	return (
		<Link to={user ? "/dashboard" : "/login?mode=register"} className={className}>
			{user ? "Přejít na dashboard" : children}
		</Link>
	);
}

function CompanySearch() {
	return (
		<CompanyAutocomplete
			formLabel="Vyhledávání firem na úvodní stránce"
			submitLabel="Najít firmu"
		/>
	);
}

function DataConvergencePreview() {
	return (
		<div className="relative mx-auto w-full max-w-xl lg:mx-0">
			<div className="pointer-events-none absolute -left-10 top-12 h-44 w-44 rounded-full bg-primary-300/30 blur-3xl" />
			<div className="pointer-events-none absolute -right-8 bottom-4 h-52 w-52 rounded-full bg-accent-300/25 blur-3xl" />

			<div className="glass-panel-strong relative rounded-3xl p-3 sm:p-4">
				<div className="rounded-2xl bg-white/75 p-5 sm:p-6">
					<div className="text-center">
						<p className="label-overline text-primary-700">Od dat k souvislostem</p>
						<h2 className="mt-2 text-xl font-semibold text-text sm:text-2xl">
							Z veřejných zdrojů do jednoho přehledu
						</h2>
						<p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-muted">
							FirmIndex propojuje dostupná data podle firmy a převádí je do použitelného kontextu.
						</p>
					</div>

					<div className="relative mt-7">
						<svg
							className="pointer-events-none absolute inset-0 hidden h-full w-full text-primary-200 md:block"
							viewBox="0 0 520 280"
							preserveAspectRatio="none"
							fill="none"
							aria-hidden="true"
						>
							<path d="M145 46 C205 46 207 140 260 140" stroke="currentColor" strokeWidth="1.5" />
							<path d="M145 108 C205 108 207 140 260 140" stroke="currentColor" strokeWidth="1.5" />
							<path d="M145 172 C205 172 207 140 260 140" stroke="currentColor" strokeWidth="1.5" />
							<path d="M145 234 C205 234 207 140 260 140" stroke="currentColor" strokeWidth="1.5" />
							<path d="M260 140 C313 140 315 46 375 46" stroke="currentColor" strokeWidth="1.5" />
							<path d="M260 140 C313 140 315 108 375 108" stroke="currentColor" strokeWidth="1.5" />
							<path d="M260 140 C313 140 315 172 375 172" stroke="currentColor" strokeWidth="1.5" />
							<path d="M260 140 C313 140 315 234 375 234" stroke="currentColor" strokeWidth="1.5" />
						</svg>

						<div className="relative grid gap-5 md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-3">
							<div>
								<p className="label-overline text-center text-muted md:text-left">Veřejné zdroje</p>
								<div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-1">
									{publicDataSources.map((source, index) => (
										<div
											key={source.label}
											className="card-tonal flex items-center gap-2 rounded-xl px-3 py-2.5"
										>
											<span
												className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
													index % 2 === 0
														? "bg-primary-50 text-primary-700"
														: "bg-secondary-50 text-secondary-700"
												}`}
											>
												<source.icon className="h-4 w-4" />
											</span>
											<span className="text-[11px] font-semibold leading-tight text-text sm:text-xs">
												{source.label}
											</span>
										</div>
									))}
								</div>
							</div>

							<ArrowDown
								className="mx-auto h-5 w-5 text-primary-400 md:hidden"
								aria-hidden="true"
							/>

							<div className="relative z-10 flex flex-col items-center">
								<div className="flex h-28 w-28 items-center justify-center rounded-full bg-white shadow-glow ring-8 ring-primary-50/80">
									<img src="/favicon-96x96.png" alt="" className="h-20 w-20" />
								</div>
								<p className="mt-3 text-base font-bold text-primary-700">FirmIndex</p>
								<p className="mt-0.5 text-center text-[10px] font-medium text-muted">
									Propojeno podle firmy
								</p>
							</div>

							<ArrowDown
								className="mx-auto h-5 w-5 text-primary-400 md:hidden"
								aria-hidden="true"
							/>

							<div>
								<p className="label-overline text-center text-muted md:text-left">Jeden přehled</p>
								<div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-1">
									{connectedInsights.map((insight, index) => (
										<div
											key={insight.label}
											className="flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 shadow-card"
										>
											<span
												className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
													index % 2 === 0
														? "bg-accent-50 text-accent-700"
														: "bg-primary-50 text-primary-700"
												}`}
											>
												<insight.icon className="h-4 w-4" />
											</span>
											<span className="text-[11px] font-semibold leading-tight text-text sm:text-xs">
												{insight.label}
											</span>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>

					<div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-xl bg-primary-50/80 px-4 py-3 text-xs font-medium text-primary-800">
						<span className="flex items-center gap-1.5">
							<Check className="h-3.5 w-3.5" />
							Dohledatelný původ
						</span>
						<span className="flex items-center gap-1.5">
							<Check className="h-3.5 w-3.5" />
							Aktualizace podle zdroje
						</span>
					</div>
				</div>
			</div>
		</div>
	);
}

function HeroSection() {
	return (
		<section className="public-hero relative z-20 mx-3 mt-3 rounded-3xl px-5 py-14 sm:mx-4 sm:px-8 lg:px-10 lg:py-20">
			<div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1.02fr_0.98fr] lg:items-center lg:gap-16">
				<div className="relative z-10 max-w-2xl">
					<div className="inline-flex items-center gap-2 rounded-full bg-white/75 px-3.5 py-2 text-xs font-semibold text-primary-700 shadow-sm">
						<span className="h-2 w-2 rounded-full bg-secondary-500" />
						Pro účetní, poradce a finanční týmy
					</div>
					<h1
						aria-label="Firemní finance jsou jen začátek. Poznejte, co se ve firmě opravdu děje."
						className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-text sm:text-5xl lg:text-6xl"
					>
						Firemní finance jsou jen začátek.{" "}
						<span className="text-primary-600">Poznejte, co se ve firmě opravdu děje.</span>
					</h1>
					<p className="mt-6 max-w-xl text-lg leading-relaxed text-muted lg:text-xl">
						FirmIndex proměňuje účetní závěrky i firemní listiny v přehled, který dává smysl — od
						finanční kondice přes připravované fúze až po pravidla ze stanov a vztahy ve skupině.
					</p>
					<p className="mt-3 max-w-xl text-xs leading-relaxed text-muted">
						Bez zdlouhavého hledání v registrech a PDF. Rozsah se odvíjí od zveřejněných podkladů.
					</p>

					<div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
						<TrialLink className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary-500 px-6 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-600">
							Vyzkoušet Premium zdarma
							<ArrowRight className="h-4 w-4" />
						</TrialLink>
						<Link
							to="#jak-to-funguje"
							className="inline-flex h-12 items-center justify-center gap-1 rounded-full bg-white/70 px-6 text-sm font-semibold text-text shadow-sm transition-colors hover:bg-white"
						>
							Jak to funguje
							<ChevronRight className="h-4 w-4" />
						</Link>
					</div>

					<div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-muted">
						<span className="flex items-center gap-1.5">
							<Check className="h-3.5 w-3.5 text-secondary-600" />
							14 dní Premium
						</span>
						<span className="flex items-center gap-1.5">
							<Check className="h-3.5 w-3.5 text-secondary-600" />
							Bez platební karty
						</span>
						<span className="flex items-center gap-1.5">
							<Check className="h-3.5 w-3.5 text-secondary-600" />
							Potom automaticky plán Zdarma
						</span>
					</div>

					<div className="mt-9 max-w-xl">
						<p className="mb-2 pl-2 text-xs font-semibold text-muted">
							Nebo si rovnou prohlédněte firmu
						</p>
						<CompanySearch />
					</div>
				</div>

				<DataConvergencePreview />
			</div>
		</section>
	);
}

function WorkflowSection() {
	return (
		<section id="jak-to-funguje" className="px-3 py-4 sm:px-4">
			<div className="glass-panel-strong mx-auto max-w-7xl rounded-3xl px-5 py-14 sm:px-8 lg:px-10 lg:py-20">
				<div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16">
					<div className="max-w-lg">
						<p className="label-overline text-primary-700">Jednoduchý postup</p>
						<h2 className="mt-3 text-3xl font-bold tracking-tight text-text sm:text-4xl">
							Od IČO k použitelnému podkladu
						</h2>
						<p className="mt-4 text-base leading-relaxed text-muted">
							FirmIndex skládá dostupné veřejné informace podle jedné firmy, abyste nemuseli každý
							zdroj otevírat a procházet zvlášť.
						</p>
						<Link
							to="/search"
							className="mt-7 inline-flex items-center gap-1 text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
						>
							Vyhledat českou firmu
							<ArrowRight className="h-4 w-4" />
						</Link>
					</div>

					<div className="grid gap-4">
						{workflowSteps.map((step) => (
							<div
								key={step.number}
								className="card-surface grid gap-4 rounded-2xl p-5 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:p-6"
							>
								<div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
									<step.icon className="h-5 w-5" />
								</div>
								<div>
									<h3 className="text-base font-semibold text-text">{step.title}</h3>
									<p className="mt-1 text-sm leading-relaxed text-muted">{step.description}</p>
								</div>
								<span className="font-mono text-xs font-semibold text-gray-400">{step.number}</span>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

function DocumentInsightsSection() {
	return (
		<section id="funkce" className="px-5 py-20 sm:px-8 lg:px-10 lg:py-28">
			<div className="mx-auto max-w-7xl">
				<div className="mx-auto max-w-3xl text-center">
					<p className="label-overline text-primary-700">V čem jde FirmIndex dál</p>
					<h2 className="mt-3 text-3xl font-bold tracking-tight text-text sm:text-4xl">
						Nejen že listina existuje. Zjistěte, co v ní je.
					</h2>
					<p className="mt-4 text-base leading-relaxed text-muted">
						Registry a finanční grafy ukážou základ. FirmIndex navíc převádí obsah nefinančních
						dokumentů ze Sbírky listin do konkrétních událostí a souvislostí.
					</p>
				</div>

				<div className="mt-12 grid gap-5 lg:grid-cols-3">
					{documentInsights.map((insight, index) => (
						<article key={insight.title} className="card-surface rounded-2xl p-6 sm:p-7">
							<div
								className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
									index === 0
										? "bg-primary-50 text-primary-700"
										: index === 1
											? "bg-secondary-50 text-secondary-700"
											: "bg-accent-50 text-accent-700"
								}`}
							>
								<insight.icon className="h-5 w-5" />
							</div>
							<h3 className="mt-6 text-lg font-semibold text-text">{insight.title}</h3>
							<p className="mt-3 text-sm leading-relaxed text-muted">{insight.description}</p>
						</article>
					))}
				</div>

				<div className="mt-5 flex items-start gap-3 rounded-2xl bg-primary-50 px-5 py-4 text-primary-900 sm:items-center sm:px-6">
					<Bell className="mt-0.5 h-5 w-5 shrink-0 text-primary-600 sm:mt-0" />
					<p className="text-sm leading-relaxed">
						Událost z nově zveřejněné listiny můžeme zpracovat dříve, než se změna projeví ve
						strukturovaných údajích obchodního rejstříku.
					</p>
				</div>
			</div>
		</section>
	);
}

function TransparencySection() {
	return (
		<section className="px-3 py-4 sm:px-4">
			<div className="mx-auto grid max-w-7xl gap-10 overflow-hidden rounded-3xl bg-gray-900 px-5 py-14 text-white shadow-card sm:px-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:px-10 lg:py-20">
				<div className="max-w-2xl">
					<p className="label-overline text-primary-300">Férově o datech</p>
					<h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
						Ukazujeme i to, co data neumějí říct
					</h2>
					<p className="mt-5 text-base leading-relaxed text-gray-300">
						Finanční rozsah závisí na dokumentech zveřejněných samotnou firmou. Automaticky vytěžené
						údaje mohou obsahovat chyby, proto u nich zpřístupňujeme původ a možnost vrátit se ke
						zdrojové listině.
					</p>
					<div className="mt-6 flex items-start gap-3 rounded-2xl bg-white/10 p-4">
						<CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-primary-300" />
						<p className="text-sm leading-relaxed text-gray-200">
							FirmIndex poskytuje informační podklady. Není úvěrovým ratingem ani náhradou vlastního
							posouzení; před významným rozhodnutím ověřte údaj v původním zdroji.
						</p>
					</div>
				</div>

				<div className="grid gap-3 sm:grid-cols-2">
					{[
						"Veřejné oficiální zdroje",
						"Datum synchronizace",
						"Odkaz na původní listinu",
						"Možnost nahlásit chybu",
					].map((item) => (
						<div key={item} className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-4">
							<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
								<Check className="h-4 w-4" />
							</span>
							<span className="text-sm font-semibold text-white">{item}</span>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function TrialSection() {
	return (
		<section className="px-3 pb-3 pt-20 sm:px-4 lg:pt-28">
			<div className="bg-gradient-brand mx-auto max-w-7xl overflow-hidden rounded-3xl px-5 py-14 text-white shadow-glow sm:px-8 lg:px-10 lg:py-20">
				<div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-16">
					<div className="max-w-3xl">
						<p className="label-overline text-white/75">Vyzkoušení zdarma</p>
						<h2 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
							14 dní Premium. Bez karty a bez rizika platby.
						</h2>
						<p className="mt-5 max-w-2xl text-base leading-relaxed text-white/80 lg:text-lg">
							Projděte si rozšířené finanční přehledy a monitoring na vlastních firmách. Po skončení
							zkoušky účet automaticky pokračuje v plánu Zdarma — nic se nestrhne.
						</p>
						<div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-white/90">
							<span className="flex items-center gap-1.5">
								<Check className="h-4 w-4" /> Až 5 let dostupné finanční historie
							</span>
							<span className="flex items-center gap-1.5">
								<Check className="h-4 w-4" /> Monitoring až 100 firem
							</span>
						</div>
					</div>

					<div className="flex flex-col items-stretch gap-3 sm:flex-row lg:flex-col">
						<TrialLink className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-7 text-sm font-semibold text-primary-700 shadow-sm transition-colors hover:bg-primary-50">
							Vytvořit účet zdarma
							<ArrowRight className="h-4 w-4" />
						</TrialLink>
						<Link
							to="/cenik"
							className="inline-flex h-12 items-center justify-center rounded-full bg-white/10 px-7 text-sm font-semibold text-white ring-1 ring-inset ring-white/35 transition-colors hover:bg-white/15"
						>
							Porovnat plány
						</Link>
					</div>
				</div>
				<p className="mt-8 text-xs leading-relaxed text-white/65">
					Dostupnost jednotlivých údajů se liší podle zveřejněných podkladů a zdrojových registrů.
				</p>
			</div>
		</section>
	);
}

export default function HomepageTwo() {
	return (
		<>
			<WebSiteJsonLd />
			<HeroSection />
			<DocumentInsightsSection />
			<WorkflowSection />
			<TransparencySection />
			<TrialSection />
		</>
	);
}
